import { getLocale } from 'i18n';
import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState } from 'react'
import styles from './DocflowTable.module.scss'

const STATUS_COLORS = {
  draft:     { bg: "var(--theme-surface-muted, #f1f5f9)", color: "var(--theme-text-muted, #64748b)" },
  pending:   { bg: "var(--theme-warning-soft, #fef9c3)", color: "var(--theme-orange-text, #854d0e)" },
  signing:   { bg: "var(--theme-info-soft, #dbeafe)", color: "var(--theme-info-text, #1d4ed8)" },
  approved:  { bg: "var(--theme-success-soft, #dcfce7)", color: "var(--theme-success-text, #15803d)" },
  returned:  { bg: "var(--theme-danger-soft, #fee2e2)", color: "var(--theme-danger-text, #b91c1c)" },
  cancelled: { bg: "var(--theme-surface-muted, #f3f4f6)", color: "var(--theme-text-muted, #6b7280)" },
}

const STATUS_LABELS = {
  draft: "Черновик",
  pending: "На рассмотрении",
  signing: "На подписании",
  approved: "Утверждён",
  returned: "Возвращён",
  cancelled: "Отменён",
}

function fmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return str
  return d.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const DOC_TYPE_ICONS = {
  business_plan: '📊', application: '📝', appeal: '📣',
  service_note: '📋', notification: '🔔', order: '📜',
  instruction: '📖', report: '📈', contract: '🤝',
  reference: '📄', memo: '🗒️', explanatory: '✍️',
  protocol: '🏛️', other: '📂',
}

export default function DocflowTable({ docs, mode, onSelect }) {
  useInterfaceLanguage();
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch]             = useState('')

  const filtered = (docs || []).filter(d => {
    if (statusFilter && d.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        d.subject?.toLowerCase().includes(q) ||
        d.reg_number?.toLowerCase().includes(q) ||
        d.from_employee_name?.toLowerCase().includes(q) ||
        d.to_employee_name?.toLowerCase().includes(q)
      )
    }
    return true
  })

  const unreadCount = (docs || []).filter(d => !d.is_read).length

  return (
    <div className={styles.wrap}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        {tr(unreadCount > 0 && (
          <span className={styles.unreadBadge}>{tr(unreadCount)}{tr(" непрочитанных")}</span>
        ))}
        <input
          className={styles.search}
          placeholder={tr("Поиск по теме, номеру, заявителю…")}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={styles.filter}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">{tr("Все статусы")}</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{tr(v)}</option>
          ))}
        </select>
      </div>

      {/* ── Table ── */}
      {tr(!docs && <p className={styles.loading}>{tr("Загрузка…")}</p>)}
      {tr(docs && !filtered.length && (
        <div className={styles.empty}>
          <span>📭</span>
          <p>{search || statusFilter ? tr("Ничего не найдено") : tr("Документов нет")}</p>
        </div>
      ))}
      {tr(docs && filtered.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 24 }}></th>
                <th>{tr("Номер")}</th>
                <th>{mode === 'outbox' ? tr("Получатель") : tr("Заявитель")}</th>
                <th>{tr("Тема")}</th>
                <th>{tr("Статус")}</th>
                {tr(mode === 'outbox' && <th>{tr("Подписание")}</th>)}
                <th>{tr("Дата")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => {
                const unread   = !doc.is_read
                const sc       = STATUS_COLORS[doc.status] || STATUS_COLORS.draft
                const typeIcon = DOC_TYPE_ICONS[doc.doc_type] || '📄'
                const party    = mode === 'outbox'
                  ? doc.to_employee_name
                  : doc.from_employee_name
                const partyDept = mode === 'outbox' ? '' : doc.from_employee_dept

                return (
                  <tr
                    key={doc.id}
                    className={`${styles.row} ${unread ? styles.unread : ''}`}
                    onClick={() => onSelect(doc.id)}
                  >
                    {/* unread dot */}
                    <td className={styles.dotCell}>
                      {tr(unread && <span className={styles.dot} />)}
                    </td>

                    {/* reg number + type */}
                    <td className={styles.numCell}>
                      <span className={styles.typeIcon}>{tr(typeIcon)}</span>
                      <div>
                        <span className={styles.regNum}>{tr(doc.reg_number)}</span>
                        <span className={styles.docType}>{tr(doc.doc_type_display)}</span>
                      </div>
                    </td>

                    {/* party */}
                    <td className={styles.partyCell}>
                      <span className={styles.partyName}>{tr(party)}</span>
                      {tr(partyDept && <span className={styles.partyDept}>{tr(partyDept)}</span>)}
                    </td>

                    {/* subject */}
                    <td className={styles.subjectCell}>
                      <span className={unread ? styles.subjectBold : styles.subjectNormal}>
                        {tr(doc.subject)}
                      </span>
                    </td>

                    {/* status badge */}
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{ background: sc.bg, color: sc.color }}
                      >
                        {tr(doc.status_display)}
                      </span>
                    </td>

                    {/* signing progress (outbox only) */}
                    {tr(mode === 'outbox' && (
                      <td className={styles.signingCell}>
                        {doc.signers_count > 0 ? (
                          <span className={styles.sigProgress}>
                            {tr(doc.signed_count)}/{tr(doc.signers_count)}{tr(" подписали")}</span>
                        ) : (
                          <span className={styles.noSig}>—</span>
                        )}
                      </td>
                    ))}

                    {/* date */}
                    <td className={styles.dateCell}>{fmtDate(doc.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
